import React from 'react';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import MessageIcon from 'four-nets/icons/svg/icon_message-4.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';

import query, { RelatedThreadsQuery, RelatedThreadsQueryVariables } from './related-threads.graphql';
import defaultStyles from './related-threads.scss';

export interface Props {
    styles: typeof defaultStyles;
    threadId: number;
    threadSlug: string;
    threadTitle: string;
}

const RelatedThreads: React.FC<Props> = React.memo((props: Props) => {
    const { styles, threadId, threadSlug, threadTitle } = props;

    const { loading, data } = useQuery<RelatedThreadsQuery, RelatedThreadsQueryVariables>(query, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-first',
        variables: {
            title: threadTitle,
            excludeId: threadId,
            categorySlug: threadSlug,
        },
    });

    const relatedThreads = data && data.relatedThreads && data.relatedThreads.edges;

    if (!relatedThreads || relatedThreads.length === 0) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.wrapperTitle}>
                <FormattedMessage id='FORUM_SIMILAR_DISCUSSIONS' />
            </div>
            <LoadingWrapperView loading={loading}>
                {relatedThreads.map((edge, index) => {
                    if (!edge || !edge.node || !edge.node.lastUpdate || !edge.node.url) {
                        return null;
                    }

                    const { id, url, threadTitle, lastUpdate, messageCount } = edge.node;
                    const isNotLast = relatedThreads.length - 1 !== index;
                    return (
                        <React.Fragment>
                            <div className={styles.row} key={id}>
                                <Link className={styles.link} to={url}>
                                    <span className={styles.threadTitle}>{threadTitle}</span>
                                </Link>
                                <div className={styles.threadInfoWrapper}>
                                    <span className={styles.lastUpdate}>
                                        <FormattedDateAgoView value={new Date(lastUpdate)} />
                                    </span>
                                    <SVGIcon style={{ image: styles.messageIcon }} src={MessageIcon} />
                                    <span className={styles.messageCount}>{messageCount}</span>
                                </div>
                            </div>
                            {isNotLast ? <div className={styles.divider} /> : null}
                        </React.Fragment>
                    );
                })}
            </LoadingWrapperView>
        </div>
    );
});

export default Styled(defaultStyles)(RelatedThreads);
